/**
 * Subscription perks visual rows for Boost cart.
 * This is display-only and does not modify actual cart line items.
 */
(function () {
  'use strict';

  const CONFIG_SCRIPT_ID = 'rtnu-subscription-perks-config';
  const BOOST_ROOT_SELECTOR = '#boost-cart-app';
  const BOOST_ITEMS_SELECTOR = '.boost-cart__items';
  const INJECTED_CLASS = 'rtnu-boost-perk-item';
  const REFRESH_COOLDOWN_MS = 1200;

  const state = {
    config: null,
    initialized: false,
    isRendering: false,
    suppressObserver: false,
    observer: null,
    refreshLockUntil: 0,
    rafId: null,
  };

  const normalizeString = (value) => {
    if (typeof value !== 'string') {
      return '';
    }
    return value.trim();
  };

  const normalizeUrl = (value) => {
    const url = normalizeString(value);
    if (!url) return '';

    if (url.startsWith('/') || url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    return '';
  };

  const parseConfig = () => {
    const configScript = document.getElementById(CONFIG_SCRIPT_ID);
    if (!configScript) return null;

    let rawConfig;
    try {
      rawConfig = JSON.parse(configScript.textContent);
    } catch (error) {
      console.error('[RTNU SubscriptionPerks] Invalid config JSON', error);
      return null;
    }

    const defaultTagText = normalizeString(rawConfig.defaultTagText) || 'Free';
    const defaultDescription = normalizeString(rawConfig.defaultDescription);

    const perks = Array.isArray(rawConfig.perks)
      ? rawConfig.perks.map((perk, index) => ({
          index,
          enabled: Boolean(perk?.enabled),
          title: normalizeString(perk?.title),
          description: normalizeString(perk?.description) || defaultDescription,
          link: normalizeUrl(perk?.link),
          tagText: normalizeString(perk?.tagText) || defaultTagText,
          image: normalizeString(perk?.image),
        }))
      : [];

    return {
      enabled: Boolean(rawConfig.enabled),
      productId: normalizeString(rawConfig.productId),
      matchSubscriptionOnly: rawConfig.matchSubscriptionOnly !== false,
      defaultTagText,
      defaultDescription,
      perks,
    };
  };

  const maybeRefreshBoostCart = () => {
    if (!window.Boost?.Cart?.refresh) return;
    if (Date.now() < state.refreshLockUntil) return;

    state.refreshLockUntil = Date.now() + REFRESH_COOLDOWN_MS;
    try {
      window.Boost.Cart.refresh();
    } catch (error) {
      console.warn('[RTNU SubscriptionPerks] Boost refresh failed', error);
    }
  };

  const getCart = async () => {
    if (window.RTNU?.CartEvents?.getCart) {
      try {
        return await window.RTNU.CartEvents.getCart(true);
      } catch (error) {
        console.warn('[RTNU SubscriptionPerks] CartEvents cart fetch failed', error);
      }
    }

    try {
      const response = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!response.ok) {
        throw new Error(`Cart fetch failed: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.warn('[RTNU SubscriptionPerks] /cart.js fetch failed', error);
      return { items: [] };
    }
  };

  const hasQualifyingLine = (cart, config) => {
    const productId = Number.parseInt(config.productId, 10);
    if (!Number.isInteger(productId)) return false;

    const cartItems = Array.isArray(cart?.items) ? cart.items : [];

    for (const item of cartItems) {
      const lineProductId = Number(item?.product_id ?? item?.product?.id);
      if (lineProductId !== productId) continue;

      if (config.matchSubscriptionOnly && !item?.selling_plan_allocation) {
        continue;
      }

      return true;
    }

    return false;
  };

  const removeExistingRows = (container) => {
    const existingRows = container.querySelectorAll(`.${INJECTED_CLASS}`);
    for (const row of existingRows) {
      row.remove();
    }
  };

  const createPerkRow = (perk) => {
    const row = document.createElement('li');
    row.className = `boost-cart__item boost-cart__item--gift boost-cart__item-gift--unlocked ${INJECTED_CLASS}`;
    row.dataset.rtnuPerkIndex = String(perk.index + 1);

    const imageWrap = document.createElement('div');
    imageWrap.className = 'boost-cart__item-image';

    if (perk.image) {
      const image = document.createElement('img');
      image.src = perk.image;
      image.alt = perk.title;
      image.width = 80;
      image.height = 80;
      image.loading = 'lazy';
      imageWrap.append(image);
    } else {
      const placeholder = document.createElement('div');
      placeholder.className = 'rtnu-boost-perk-item__image-placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      imageWrap.append(placeholder);
    }

    const info = document.createElement('div');
    info.className = 'boost-cart__item-info';

    const infoLeft = document.createElement('div');
    infoLeft.className = 'boost-cart__item-info-left';

    const titleEl = perk.link ? document.createElement('a') : document.createElement('div');
    titleEl.className = perk.link
      ? 'boost-cart__item-title'
      : 'boost-cart__item-title boost-cart__item-title--no-link';
    titleEl.textContent = perk.title;
    if (perk.link) {
      titleEl.setAttribute('href', perk.link);
    }
    infoLeft.append(titleEl);

    if (perk.description) {
      const descriptionEl = document.createElement('div');
      descriptionEl.className = 'boost-cart__item-message';
      descriptionEl.textContent = perk.description;
      infoLeft.append(descriptionEl);
    }

    const infoRight = document.createElement('div');
    infoRight.className = 'boost-cart__item-info-right';

    const giftTag = document.createElement('div');
    giftTag.className = 'boost-cart__item-gift-tag';
    giftTag.textContent = perk.tagText;
    infoRight.append(giftTag);

    info.append(infoLeft, infoRight);
    row.append(imageWrap, info);

    return row;
  };

  const ensureObserver = () => {
    if (state.observer) return;

    const boostRoot = document.querySelector(BOOST_ROOT_SELECTOR);
    if (!boostRoot) return;

    state.observer = new MutationObserver(() => {
      if (state.suppressObserver) return;
      scheduleRender('mutation');
    });

    state.observer.observe(boostRoot, { childList: true, subtree: true });
  };

  const render = async (source = 'manual') => {
    if (state.isRendering) return;
    state.isRendering = true;

    try {
      state.config = state.config || parseConfig();
      if (!state.config?.enabled) return;

      ensureObserver();

      const perks = state.config.perks.filter((perk) => perk.enabled && perk.title);
      const cart = await getCart();
      const shouldShowPerks = perks.length > 0 && hasQualifyingLine(cart, state.config);
      const containers = document.querySelectorAll(BOOST_ITEMS_SELECTOR);

      if (containers.length === 0) {
        if (shouldShowPerks && source !== 'mutation') {
          maybeRefreshBoostCart();
        }
        return;
      }

      state.suppressObserver = true;
      for (const container of containers) {
        removeExistingRows(container);
        if (!shouldShowPerks) continue;

        const fragment = document.createDocumentFragment();
        for (const perk of perks) {
          fragment.append(createPerkRow(perk));
        }
        container.append(fragment);
      }
    } catch (error) {
      console.error('[RTNU SubscriptionPerks] Render failed', error);
    } finally {
      state.isRendering = false;
      requestAnimationFrame(() => {
        state.suppressObserver = false;
      });
    }
  };

  const scheduleRender = (source) => {
    if (state.rafId) {
      cancelAnimationFrame(state.rafId);
    }

    state.rafId = requestAnimationFrame(() => {
      state.rafId = null;
      render(source);
    });
  };

  const init = () => {
    if (state.initialized) return;
    state.initialized = true;

    state.config = parseConfig();
    if (!state.config?.enabled) return;

    document.addEventListener('boost:cart.loaded', () => scheduleRender('boost:cart.loaded'));
    document.addEventListener('cart:updated', () => scheduleRender('cart:updated'));
    window.addEventListener('pageshow', () => scheduleRender('pageshow'));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        scheduleRender('visibilitychange');
      }
    });

    scheduleRender('init');

    window.RTNU = window.RTNU || {};
    window.RTNU.SubscriptionPerks = {
      refresh() {
        scheduleRender('manual');
      },
    };
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
